import {
    collection,
    doc,
    getDocs,
    addDoc,
    updateDoc,
    deleteDoc,
    query,
    orderBy,
    where,
    Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { Promotion, CreatePromotionForm } from '../types';

export class PromotionService {
    private static readonly COLLECTION = 'promotions';

    static async getPromotions(activeOnly: boolean = false): Promise<Promotion[]> {
        try {
            const promotionsRef = collection(db, this.COLLECTION);
            let q = query(promotionsRef, orderBy('createdAt', 'desc'));

            if (activeOnly) {
                q = query(promotionsRef,
                    where('isActive', '==', true),
                    orderBy('createdAt', 'desc')
                );
            }

            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                    id: doc.id,
                    ...data,
                    startDate: data.startDate?.toDate?.() || new Date(),
                    endDate: data.endDate?.toDate?.() || new Date(),
                    createdAt: data.createdAt?.toDate?.() || new Date(),
                    updatedAt: data.updatedAt?.toDate?.() || new Date(),
                } as Promotion;
            });
        } catch (error: any) {
            throw new Error(`Failed to fetch promotions: ${error.message}`);
        }
    }

    static async createPromotion(data: CreatePromotionForm): Promise<string> {
        try {
            const promotionsRef = collection(db, this.COLLECTION);
            const docRef = await addDoc(promotionsRef, {
                ...data,
                isActive: true,
                usageCount: 0,
                startDate: Timestamp.fromDate(data.startDate),
                endDate: Timestamp.fromDate(data.endDate),
                createdAt: Timestamp.now(),
                updatedAt: Timestamp.now(),
            });
            return docRef.id;
        } catch (error: any) {
            throw new Error(`Failed to create promotion: ${error.message}`);
        }
    }

    static async updatePromotionStatus(id: string, isActive: boolean): Promise<void> {
        try {
            const promotionRef = doc(db, this.COLLECTION, id);
            await updateDoc(promotionRef, {
                isActive,
                updatedAt: Timestamp.now(),
            });
        } catch (error: any) {
            throw new Error(`Failed to update promotion status: ${error.message}`);
        }
    }

    static async deletePromotion(id: string): Promise<void> {
        try {
            const promotionRef = doc(db, this.COLLECTION, id);
            await deleteDoc(promotionRef);
        } catch (error: any) {
            throw new Error(`Failed to delete promotion: ${error.message}`);
        }
    }
}
